import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import AppLayout from '../components/layout/AppLayout';
import { useTimezone, shiftStatus, accountStatus, SHIFT, ACTION } from '@kioskgaming/ui';
import { formatInTimezone } from '@kioskgaming/ui';
import {
  fetchCashierReport,
  fetchCashierShifts,
  fetchFraudIndicators,
  fetchShopCashiers,
  resetShopCashierPassword,
  suspendShopCashier,
  terminateShopCashier,
  unsuspendShopCashier,
  updateShopCashier,
} from '../services/api';
import type { CashierLoginMethod, ShopCashier } from '../types';
import { currentShiftBadgeKind, currentShiftBadgeLabel, scheduleShiftPath } from '../utils/cashierSchedule';

const LOGIN_METHOD_OPTIONS: { value: CashierLoginMethod; label: string }[] = [
  { value: 'any', label: 'Any (username, email or phone)' },
  { value: 'username', label: 'Username only' },
  { value: 'email', label: 'Email only' },
  { value: 'phone', label: 'Phone only' },
];

function loginMethodLabel(method: CashierLoginMethod | string | undefined) {
  return LOGIN_METHOD_OPTIONS.find((o) => o.value === method)?.label || method || '—';
}

export default function CashierDetailPage() {
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const { cashierId } = useParams();
  const [rows, setRows] = useState<ShopCashier[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [editForm, setEditForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    loginMethod: 'any' as CashierLoginMethod,
    note: '',
  });

  const [report, setReport] = useState<any>(null);
  const [shifts, setShifts] = useState<any[]>([]);
  const [fraud, setFraud] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const id = cashierId ? decodeURIComponent(cashierId) : '';

  const getApiErrorMessage = (e: unknown) => {
    const anyE = e as any;
    return anyE?.response?.data?.message_localized || anyE?.response?.data?.message || anyE?.message || null;
  };

  const selectedRow = useMemo(() => rows.find((r) => r.id === id) || null, [rows, id]);

  const loadCashiers = async () => {
    setLoading(true);
    try {
      const res = await fetchShopCashiers();
      if (!res.success) throw new Error(res.message || 'Failed to load Cashiers');
      setRows((res.data || []) as ShopCashier[]);
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Failed to load Cashiers');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async () => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const [r, s, f] = await Promise.all([
        fetchCashierReport(id, { period: 'daily' }),
        fetchCashierShifts(id),
        fetchFraudIndicators({ period: 'daily' }),
      ]);
      setReport(r.success ? r.data : null);
      setShifts(s.success ? (s.data || []) : []);
      setFraud(f.success ? f.data : null);
    } catch {
      setReport(null);
      setShifts([]);
      setFraud(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    void loadCashiers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashierId]);

  useEffect(() => {
    void loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onUnsuspend = async () => {
    if (!selectedRow) return;
    if (!window.confirm('Reactivate this Cashier? They can sign in again.')) return;
    try {
      const res = await unsuspendShopCashier(selectedRow.id);
      if (!res.success) throw new Error(res.message || 'Reactivate failed');
      toast.success('Cashier reactivated');
      await loadCashiers();
      await loadDetail();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Reactivate failed');
    }
  };

  const onResetPassword = async () => {
    if (!selectedRow) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetting(true);
    try {
      const res = await resetShopCashierPassword(selectedRow.id, newPassword);
      if (!res.success) throw new Error(res.message || 'Reset password failed');
      toast.success('Password reset');
      setResetOpen(false);
      setNewPassword('');
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Reset password failed');
    } finally {
      setResetting(false);
    }
  };

  const onSuspend = async () => {
    if (!selectedRow) return;
    if (!window.confirm('Suspend this Cashier? This will auto-close their shift and invalidate sessions.')) return;
    try {
      const res = await suspendShopCashier(selectedRow.id);
      if (!res.success) throw new Error(res.message || 'Suspend failed');
      toast.success('Cashier suspended');
      await loadCashiers();
      await loadDetail();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Suspend failed');
    }
  };

  const openEditProfile = () => {
    if (!selectedRow) return;
    setEditForm({
      fullName: selectedRow.fullName || selectedRow.name || '',
      email: selectedRow.email || '',
      phone: selectedRow.phone || '',
      loginMethod: selectedRow.loginMethod || 'any',
      note: '',
    });
    setEditOpen(true);
  };

  const onSaveProfile = async () => {
    if (!selectedRow) return;
    if (!editForm.fullName.trim()) {
      toast.error('Full name is required');
      return;
    }
    if (!editForm.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!editForm.phone.trim()) {
      toast.error('Phone is required');
      return;
    }

    setSavingProfile(true);
    try {
      const res = await updateShopCashier(selectedRow.id, {
        fullName: editForm.fullName.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim(),
        loginMethod: editForm.loginMethod,
        ...(editForm.note.trim() ? { note: editForm.note.trim() } : {}),
      });
      if (!res.success) throw new Error(res.message || 'Update profile failed');
      toast.success('Profile updated');
      setEditOpen(false);
      await loadCashiers();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Update profile failed');
    } finally {
      setSavingProfile(false);
    }
  };

  const onTerminate = async () => {
    if (!selectedRow) return;
    if (!window.confirm('Terminate this Cashier? This will auto-close their shift and invalidate sessions.')) return;
    try {
      const res = await terminateShopCashier(selectedRow.id);
      if (!res.success) throw new Error(res.message || 'Terminate failed');
      toast.success('Cashier terminated');
      await loadCashiers();
      await loadDetail();
    } catch (e: unknown) {
      toast.error(getApiErrorMessage(e) || 'Terminate failed');
    }
  };

  return (
    <AppLayout contentFullWidth>
      <div className="min-w-0 max-w-none">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
              onClick={() => navigate('/cashiers')}
            >
              Back
            </button>
            <h1 className="text-xl font-semibold">Cashier details</h1>
          </div>
          <button
            type="button"
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
            onClick={() => {
              setActionsOpen(false);
              void loadCashiers();
              void loadDetail();
            }}
          >
            Refresh
          </button>
        </div>

        <div className="mt-3 rounded-xl border border-stone-200 bg-white p-4">
          {!id ? (
            <div className="text-sm text-stone-600">Invalid Cashier id.</div>
          ) : loading && !rows.length ? (
            <div className="text-sm text-stone-600">Loading…</div>
          ) : !selectedRow ? (
            <div className="text-sm text-stone-600">
              Cashier not found.{' '}
              <button type="button" className="font-semibold text-emerald-700 hover:underline" onClick={() => navigate('/cashiers')}>
                Go back
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">{selectedRow.name}</h2>
                  <div className="text-sm text-stone-600">
                    Status: <span className="font-medium">{accountStatus(selectedRow.status).label}</span>
                    {' • '}
                    {currentShiftBadgeLabel(selectedRow.currentShift?.status)}
                    {selectedRow.lastLoginAt
                      ? ` • Last login: ${formatInTimezone(selectedRow.lastLoginAt, timezone)}`
                      : ''}
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {selectedRow.status === 'active' ? (
                    <button
                      type="button"
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                      onClick={() => navigate(scheduleShiftPath(selectedRow.id))}
                    >
                      {SHIFT.scheduleShift}
                    </button>
                  ) : null}
                  {selectedRow.currentShift?.id ? (
                    <button
                      type="button"
                      className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                      onClick={() => navigate(`/shifts/${encodeURIComponent(selectedRow.currentShift!.id)}`)}
                    >
                      View current shift
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={openEditProfile}
                    disabled={selectedRow.status === 'terminated'}
                  >
                    Edit profile
                  </button>
                  <div className="relative">
                    <button
                      type="button"
                      className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                      onClick={() => setActionsOpen((o) => !o)}
                      aria-expanded={actionsOpen}
                      aria-haspopup="true"
                    >
                      Actions
                    </button>
                    {actionsOpen ? (
                      <div className="absolute right-0 top-[calc(100%+8px)] z-20 w-48 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-lg">
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm font-medium text-stone-800 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => {
                            setActionsOpen(false);
                            setResetOpen(true);
                          }}
                          disabled={selectedRow.status === 'terminated'}
                        >
                          Reset password
                        </button>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => {
                            setActionsOpen(false);
                            void onUnsuspend();
                          }}
                          disabled={selectedRow.status !== 'suspended'}
                        >
                          {ACTION.reactivate}
                        </button>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm font-medium text-stone-800 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => {
                            setActionsOpen(false);
                            void onSuspend();
                          }}
                          disabled={selectedRow.status !== 'active'}
                        >
                          Suspend
                        </button>
                        <button
                          type="button"
                          className="w-full px-3 py-2 text-left text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={() => {
                            setActionsOpen(false);
                            void onTerminate();
                          }}
                          disabled={selectedRow.status === 'terminated'}
                        >
                          Terminate
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-stone-200 p-3">
                <div className="text-xs font-medium uppercase tracking-wide text-stone-500">Profile</div>
                <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-stone-500">Full name</dt>
                    <dd className="font-medium text-stone-900">{selectedRow.fullName || selectedRow.name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-500">Username</dt>
                    <dd className="font-medium text-stone-900">{selectedRow.username || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-500">Email</dt>
                    <dd className="font-medium text-stone-900">{selectedRow.email || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-stone-500">Phone</dt>
                    <dd className="font-medium text-stone-900">{selectedRow.phone || '—'}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-stone-500">Login method</dt>
                    <dd className="font-medium text-stone-900">{loginMethodLabel(selectedRow.loginMethod)}</dd>
                  </div>
                </dl>
              </div>

              <div className="mt-4 grid gap-4 lg:grid-cols-3">
                <div className="rounded-lg border border-stone-200 p-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-stone-500">Daily totals</div>
                  <div className="mt-2 text-sm text-stone-700">
                    {detailLoading ? (
                      'Loading…'
                    ) : (
                      <>
                        <div>
                          Deposits: <b>{report?.totals?.deposits?.total ?? 0}</b> ({report?.totals?.deposits?.count ?? 0})
                        </div>
                        <div>
                          Redemptions: <b>{report?.totals?.redemptions?.total ?? 0}</b> ({report?.totals?.redemptions?.count ?? 0})
                        </div>
                        <div>
                          Withdrawals: <b>{report?.totals?.withdrawals?.total ?? 0}</b> ({report?.totals?.withdrawals?.count ?? 0})
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="rounded-lg border border-stone-200 p-3 lg:col-span-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-medium uppercase tracking-wide text-stone-500">Shift history</div>
                    {selectedRow.status === 'active' ? (
                      <button
                        type="button"
                        className="text-xs font-semibold text-emerald-700 hover:underline"
                        onClick={() => navigate(scheduleShiftPath(selectedRow.id))}
                      >
                        + Schedule shift
                      </button>
                    ) : null}
                  </div>
                  {currentShiftBadgeKind(selectedRow.currentShift?.status) === 'none' && selectedRow.status === 'active' ? (
                    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      No scheduled or open shift. Schedule one before this Cashier can deposit or redeem.
                    </div>
                  ) : null}
                  <div className="mt-2 max-h-56 overflow-y-auto text-sm">
                    {detailLoading ? (
                      'Loading…'
                    ) : shifts.length ? (
                      shifts.map((s) => {
                        const flagged = !!s.discrepancyFlagged;
                        const locked = s.status === 'auto_locked';
                        const label =
                          s.status ||
                          (s.closedAt ? 'closed' : 'open');
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => navigate(`/shifts/${encodeURIComponent(s.id)}`)}
                            className={`mb-2 w-full rounded-md border px-3 py-2 text-left ${
                              flagged || locked ? 'border-rose-200 bg-rose-50' : 'border-stone-200 hover:bg-stone-50'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-medium">
                                {s.shiftDate ||
                                  (s.scheduledStartAt
                                    ? formatInTimezone(s.scheduledStartAt, timezone)
                                    : formatInTimezone(s.openedAt, timezone))}
                              </div>
                              <div className="text-xs font-semibold text-stone-600">
                                {shiftStatus(String(label)).label}
                              </div>
                            </div>
                            <div className="mt-1 text-xs text-stone-700">
                              Opening: {s.openingCashBalance ?? 0} • Expected: {s.expectedClosingCash ?? '—'} • Counted:{' '}
                              {s.closingCashCounted ?? '—'} • Discrepancy: {s.discrepancyAmount ?? '—'}
                            </div>
                            {locked ? (
                              <div className="mt-1 text-xs text-rose-700">
                                Auto-locked. Reconcile this shift, then assign a new one.
                              </div>
                            ) : null}
                          </button>
                        );
                      })
                    ) : (
                      <div className="text-stone-600">No shifts yet.</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-stone-200 p-3">
                <div className="text-xs font-medium uppercase tracking-wide text-stone-500">Fraud indicators (daily)</div>
                <div className="mt-2 text-sm text-stone-700">
                  {detailLoading ? (
                    'Loading…'
                  ) : (
                    <>
                      <div>
                        <b>High redemptions:</b> {fraud?.highRedemptions?.length ? fraud.highRedemptions.length : 0}
                      </div>
                      <div>
                        <b>Discrepancy shifts:</b> {fraud?.discrepancies?.length ? fraud.discrepancies.length : 0}
                      </div>
                      <div>
                        <b>Outside shift hours:</b> {fraud?.outsideShiftHours?.length ? fraud.outsideShiftHours.length : 0}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {editOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
            <div className="text-lg font-semibold">Edit profile</div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-stone-700">Full name</label>
                <input
                  value={editForm.fullName}
                  onChange={(e) => setEditForm((f) => ({ ...f, fullName: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Email</label>
                <input
                  value={editForm.email}
                  onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  type="email"
                />
              </div>
              <div>
                <label htmlFor="cashier-edit-phone" className="text-sm font-medium text-stone-700">Phone</label>
                <input
                  id="cashier-edit-phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  type="tel"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Login method</label>
                <select
                  value={editForm.loginMethod}
                  onChange={(e) => setEditForm((f) => ({ ...f, loginMethod: e.target.value as CashierLoginMethod }))}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                >
                  {LOGIN_METHOD_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700">Note (optional)</label>
                <textarea
                  value={editForm.note}
                  onChange={(e) => setEditForm((f) => ({ ...f, note: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                  rows={2}
                />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                onClick={() => setEditOpen(false)}
                disabled={savingProfile}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={savingProfile}
                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                onClick={() => void onSaveProfile()}
              >
                {savingProfile ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {resetOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
            <div className="text-lg font-semibold">Reset password</div>
            <p className="mt-1 text-sm text-stone-600">
              Set a new password for {selectedRow?.name}. All active sessions will be invalidated.
            </p>
            <div className="mt-4">
              <label className="text-sm font-medium text-stone-700">New password</label>
              <input
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 outline-none focus:border-emerald-500"
                placeholder="••••••"
                type="password"
              />
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold hover:bg-stone-50"
                onClick={() => {
                  setResetOpen(false);
                  setNewPassword('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={resetting}
                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                onClick={() => void onResetPassword()}
              >
                {resetting ? 'Saving…' : 'Reset'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AppLayout>
  );
}

